import { useMemo } from 'react';

import { getAvailableMailingProducts, type MailingProduct, type MailingType } from '@/services/ws-post/src/main/resources/rest-api';
import type { DateTimeUnixType } from '@/js/libs/api-helpers';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { useFeatureToggleContext } from 'src/modules/FeatureToggle';
import { getPostDtoDate } from '../helpers';
import { getProductsForMailingType } from '../mailingProduct';

export const useAvailableMailingProducts = (
  mailingType: MailingType | undefined,
  distributionDate?: DateTimeUnixType,
): { products: MailingProduct[]; pending: boolean } => {
  const { isAdditionalPromopostProductsEnabled } = useFeatureToggleContext();

  const requestParams = useMemo(() => {
    if (!isAdditionalPromopostProductsEnabled) {
      return {};
    }

    const distributionDateParam = getPostDtoDate(distributionDate);

    if (!distributionDateParam) {
      return {};
    }

    return { distributionDate: distributionDateParam };
  }, [distributionDate, isAdditionalPromopostProductsEnabled]);

  const availableMailingProductsRequest = useRequest({
    apiMethod: getAvailableMailingProducts,
    params: requestParams,
  });

  const products = useSubscribe(availableMailingProductsRequest.data$, (response) =>
    getProductsForMailingType(response?.data, mailingType),
  );
  const pending = useSubscribe(availableMailingProductsRequest.pending$);

  return { products, pending };
};
